import axios from "axios";
import { getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

export interface ApprovalRuleOperator {
  approval_rule_operator_id: number;
  approval_rule_operator_key: string;
  approval_rule_operator_display_name: string;
  operator: string;
  input_type: string;
  description: string;
}

export interface ApprovalActorType {
  approval_actor_type_id: number;
  approval_actor_type_key: string;
  approval_actor_type_display_name: string;
}

export interface ApprovalStageCondition {
  approval_stage_condition_id: number;
  approval_stage_condition_key: string;
  approval_stage_condition_display_name: string;
}

export interface EntityField {
  field_name: string;
  display_name: string;
  data_type: string;
  allowed_operators: string[];
}

export interface EntityFieldResponse {
  entity_name: string;
  fields: EntityField[];
}

export interface ApprovalPolicyAPI {
  approval_policy_id: number | string;
  approval_policy_name: string;
  related_entity_name: string;
  is_active: boolean;
  is_manager_approval_needed: boolean;
  priority_order?: number;
  created_datetime?: string;
  updated_datetime?: string;
  tenant_id?: number | string;
  created_by?: number;
  updated_by?: number;
}

export interface ApprovalPolicyCreatePayload {
  approval_policy_name: string;
  is_active: boolean;
  is_manager_approval_needed: boolean;
  priority_order: number;
  related_entity_name: string;
  tenant_id: number | string;
  created_by: number;
  updated_by: number;
}

export interface ApprovalPolicyUpdatePayload {
  approval_policy_name?: string;
  is_active?: boolean;
  tenant_id?: number | string;
  is_manager_approval_needed?: boolean;
  priority_order?: number;
  related_entity_name?: string;
  updated_by?: number;
}

export interface ApprovalRuleCreatePayload {
  tenant_id: number | string;
  approval_policy_id: number | string;
  related_entity_name: string;
  entity_field_name: string;
  operator_key: string;
  value_json: Record<string, any>;
  priority: number;
  created_by: number;
  updated_by: number;
  rule_group_key: string;
  rule_join_key: string | null;
}

export interface ApprovalRuleAPI {
  approval_policy_rule_id: number | string;
  approval_policy_id: number | string;
  related_entity_name: string;
  entity_field_name: string;
  operator_key: string;
  value_json: Record<string, any>;
  priority: number;
  rule_group_key: string;
  rule_join_key: string | null;
  tenant_id?: number | string;
  created_by?: number;
  updated_by?: number;
}

export interface ApprovalPolicyStageCreatePayload {
  tenant_id: number | string;
  approval_policy_id: number | string;
  stage_order: number;
  stage_name: string;
  approval_stage_condition_id: number;
}

export interface ApprovalPolicyStageAPI {
  approval_policy_stage_id: number | string;
  tenant_id: number | string;
  approval_policy_id: number | string;
  stage_order: number;
  stage_name: string;
  approval_stage_condition_id: number;
}

export interface ApprovalPolicyStageApproverPayload {
  tenant_id: number | string;
  approval_policy_stage_id: number | string;
  approval_actor_type_id: number | string;
  approver_identities: (number | string)[];
}

export interface TenantUser {
  tenant_user_id: number;
  tenat_name: string;
  domain_name: string;
  user_first_name: string;
  user_last_name: string;
  role_display_name: string;
  email: string;
  avatar_url: string | null;
  created_datetime: string;
  updated_datetime: string;
  is_active: boolean;
  tenant_id: number;
  user_id: number;
  role_id: number;
}

export interface ApprovalStageApproverUser {
  user_id: number | string;
  full_name: string | null;
  avatar_url: string | null;
  email: string | null;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ApprovalPolicyDetailAPI {
  approval_policy_id: number;
  approval_policy_name: string;
  related_entity_name: string;
  is_active: boolean;
  is_manager_approval_needed: boolean;
  created_datetime: string;
  updated_datetime: string;
  tenant_id: number;
  created_by: number;
  updated_by: number;
  rules: ApprovalRuleAPI[];
  stages: {
    approval_policy_stage_id: number;
    stage_order: number;
    stage_name: string;
    created_datetime: string;
    tenant_id: number;
    approval_policy_id: number;
    approval_stage_condition_id: number;
    approval_stage_condition: {
      approval_stage_condition_id: number;
      approval_stage_condition_key: string;
      approval_stage_condition_display_name: string;
    };
  }[];
  stage_approvers: {
    approval_policy_stage_approver_id: number;
    approver_identities: (string | number)[];
    tenant_id: number;
    approval_policy_stage_id: number;
    approval_actor_type_id: number;
    approval_actor_type: {
      approval_actor_type_id: number;
      approval_actor_type_key: string;
      approval_actor_type_display_name: string;
    };
    approvers: ApprovalStageApproverUser[];
  }[];
}

export const ApprovalPolicyService = {
  fetchPolicies: async (tenantId: string | number): Promise<ApprovalPolicyAPI[]> => {
    const response = await axios.get<PaginatedResponse<ApprovalPolicyAPI>>(
      `${API_BASE_URL}/approval-policy/?tenant_id=${tenantId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchPolicyDetail: async (tenantId: string | number, policyId: string | number): Promise<ApprovalPolicyDetailAPI> => {
    const response = await axios.get<ApprovalPolicyDetailAPI>(
      `${API_BASE_URL}/approval-policy/${policyId}/detail/?tenant_id=${tenantId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  createPolicy: async (payload: ApprovalPolicyCreatePayload): Promise<ApprovalPolicyAPI> => {
    const response = await axios.post<ApprovalPolicyAPI>(
      `${API_BASE_URL}/approval-policy/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  updatePolicy: async (
    id: string | number,
    payload: ApprovalPolicyUpdatePayload
  ): Promise<ApprovalPolicyAPI> => {
    const response = await axios.patch<ApprovalPolicyAPI>(
      `${API_BASE_URL}/approval-policy/${id}/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  deletePolicy: async (id: string | number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/approval-policy/${id}/`, {
      headers: getAuthHeaders(),
    });
  },

  fetchEntityFields: async (entityName: string): Promise<EntityField[]> => {
    const response = await axios.get<EntityFieldResponse>(
      `${API_BASE_URL}/lookup/entity-field/?entity_name=${entityName}`,
      { headers: getAuthHeaders() }
    );
    return response.data.fields || [];
  },

  fetchRuleOperators: async (): Promise<ApprovalRuleOperator[]> => {
    const response = await axios.get<PaginatedResponse<ApprovalRuleOperator>>(
      `${API_BASE_URL}/approval-rule-operator/`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchRulesByPolicy: async (tenantId: string | number, policyId: string | number): Promise<ApprovalRuleAPI[]> => {
    const response = await axios.get<PaginatedResponse<ApprovalRuleAPI>>(
      `${API_BASE_URL}/approval-policy-rule/?tenant_id=${tenantId}&approval_policy_id=${policyId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchStagesByPolicy: async (tenantId: string | number, policyId: string | number): Promise<ApprovalPolicyStageAPI[]> => {
    const response = await axios.get<PaginatedResponse<ApprovalPolicyStageAPI>>(
      `${API_BASE_URL}/approval-policy-stage/?tenant_id=${tenantId}&approval_policy_id=${policyId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchStageApprovers: async (tenantId: string | number, stageId: string | number): Promise<any[]> => {
    const response = await axios.get<PaginatedResponse<any>>(
      `${API_BASE_URL}/approval-policy-stage-approver/?tenant_id=${tenantId}&approval_policy_stage_id=${stageId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  createRule: async (payload: ApprovalRuleCreatePayload): Promise<ApprovalRuleAPI> => {
    const response = await axios.post<ApprovalRuleAPI>(
      `${API_BASE_URL}/approval-policy-rule/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  updateRule: async (id: string | number, payload: Partial<ApprovalRuleCreatePayload>): Promise<ApprovalRuleAPI> => {
    const response = await axios.patch<ApprovalRuleAPI>(
      `${API_BASE_URL}/approval-policy-rule/${id}/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  deleteRule: async (id: string | number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/approval-policy-rule/${id}/`, {
      headers: getAuthHeaders(),
    });
  },

  fetchActorTypes: async (): Promise<ApprovalActorType[]> => {
    const response = await axios.get<PaginatedResponse<ApprovalActorType>>(
      `${API_BASE_URL}/approval-actor-type/`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchStageConditions: async (): Promise<ApprovalStageCondition[]> => {
    const response = await axios.get<PaginatedResponse<ApprovalStageCondition>>(
      `${API_BASE_URL}/approval-stage-condition/`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  createStage: async (payload: ApprovalPolicyStageCreatePayload): Promise<ApprovalPolicyStageAPI> => {
    const response = await axios.post<ApprovalPolicyStageAPI>(
      `${API_BASE_URL}/approval-policy-stage/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  updateStage: async (id: string | number, payload: Partial<ApprovalPolicyStageCreatePayload>): Promise<ApprovalPolicyStageAPI> => {
    const response = await axios.patch<ApprovalPolicyStageAPI>(
      `${API_BASE_URL}/approval-policy-stage/${id}/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  deleteStage: async (id: string | number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/approval-policy-stage/${id}/`, {
      headers: getAuthHeaders(),
    });
  },

  saveStageApprover: async (payload: ApprovalPolicyStageApproverPayload): Promise<any> => {
    const response = await axios.post(
      `${API_BASE_URL}/approval-policy-stage-approver/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  fetchTenantUsers: async (tenantId: string | number): Promise<TenantUser[]> => {
    const response = await axios.get<PaginatedResponse<TenantUser>>(
      `${API_BASE_URL}/tenant-user/?tenant_id=${tenantId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },
};
